import { ref, nextTick } from 'vue';
import { useResizeObserver } from '@vueuse/core';

export interface CollapsibleSection {
    isOpen: Ref<boolean>;
    contentRef: Ref<HTMLElement | null>;
    contentHeight: Ref<string>;
    toggle: () => void;
}

/**
 * Composable for managing collapsible section state and animations
 * @param defaultOpen - Initial state of the section (default: false)
 * @returns Object with reactive state and toggle function
 */
export function useCollapsibleSection(defaultOpen = false): CollapsibleSection {
    const isOpen = ref(defaultOpen);
    const contentRef = ref<HTMLElement | null>(null);
    const contentHeight = ref<string>(defaultOpen ? 'auto' : '0px');
    const isAnimating = ref(false);

    const updateHeight = () => {
        if (contentRef.value) {
            contentHeight.value = `${contentRef.value.offsetHeight}px`;
        }
    };

    const toggle = () => {
        if (isAnimating.value) return;
        isAnimating.value = true;

        if (!isOpen.value) {
            // Opening: set isOpen first, then measure after content renders
            isOpen.value = true;
            nextTick(() => {
                updateHeight();
                // After the opening transition ends, switch to auto height
                // so dynamically added content (v-if, transitions) is visible
                setTimeout(() => {
                    if (isOpen.value) {
                        contentHeight.value = 'auto';
                    }
                    isAnimating.value = false;
                }, 350);
            });
        } else {
            // Closing: first set explicit height, then collapse to 0
            updateHeight();
            // Force a reflow so the browser registers the explicit height
            contentRef.value?.offsetHeight;
            requestAnimationFrame(() => {
                isOpen.value = false;
                contentHeight.value = '0px';
                setTimeout(() => {
                    isAnimating.value = false;
                }, 550);
            });
        }
    };

    useResizeObserver(contentRef, () => {
        // A pinned px height while open (content grew right after the opening
        // transition) clips the content and lets it overlap the next blocks:
        // release to auto instead of chasing the new px value.
        if (isOpen.value && !isAnimating.value && contentHeight.value !== 'auto') {
            contentHeight.value = 'auto';
        }
    });

    return {
        isOpen,
        contentRef,
        contentHeight,
        toggle,
    };
}
